// src/App.js
import React from "react";
import * as Sentry from "@sentry/react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { useLastActive } from "./hooks/useLastActive";
import useAppConfigFlags from "./hooks/useAppConfigFlags";

import ScrollToTop from "./components/ScrollToTop";
import FcmTokenManager from "./components/FcmTokenManager";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { useAuth } from "./contexts/AuthContext";
import { UserBadgesProvider } from "./contexts/UserBadgesContext";
import { db } from "./firebase";
import { getOrCreateUserProfile } from "./services/profileService";
import { claimPendingSharedCapsules } from "./services/sharedCapsuleClaimService";
import useUserProfileStore, {
  selectClearUserProfile,
  selectSetUserProfile,
  selectUserProfileId,
} from "./store/useUserProfileStore";

import MainLayout from "./components/layouts/MainLayout";
import MapLayout from "./components/layouts/MapLayout";
import PromoRedeemModalHost from "./components/promo/PromoRedeemModalHost";
import NoNavLayout from "./components/layouts/NoNavLayout";

import SignInPage from "./components/pages/auth/SignInPage";
import SignUpPage from "./components/pages/auth/SignUpPage";
import PhoneOtpPage from "./components/pages/PhoneOtpPage";

import PublicProfilePage from "./components/pages/profile/PublicProfilePage";
import ConnectionRequestsPage from "./components/pages/profile/ConnectionRequestsPage";
import MyProfileRedirectPage from "./components/pages/profile/MyProfileRedirectPage";
import ProfileSettingsPage from "./components/pages/profile/ProfileSettingsPage";
import ProfileUidRedirectPage from "./components/pages/profile/ProfileUidRedirectPage";
import HomePage from "./components/pages/HomePage";
import FirstRunOnboardingGate from "./components/onboarding/FirstRunOnboardingGate";
import WatchPage from "./components/pages/watch/WatchPage";
import SeriesPage from "./components/pages/watch/SeriesPage";
import VideoPlayerPage from "./components/pages/watch/VideoPlayerPage";
import MessagesPage from "./components/pages/message/MessagesPage";
import HistoryPage from "./components/pages/HistoryPage";
import AccountSettingsPage from "./components/pages/AccountSettingsPage";
import MembershipPage from "./components/pages/MembershipPage";
import PinDetailsPage from "./components/pages/map/PinDetailsPage";
import MyContactsPage from "./components/pages/MyContactsPage";
import PublicBusinessPage from "./components/pages/business/PublicBusinessPage";

import TimeCapsuleWizard from "./components/pages/message/timecapsule/TimeCapsuleWizard";

import ClaimLandingPage from "./components/pages/claims/ClaimLandingPage";
import ClaimTicketPage from "./components/pages/claims/ClaimTicketPage";
import ClaimsPage from "./components/pages/claims/ClaimsPage";

import PointsLedgerPage from "./components/pages/PointsLedgerPage";

// NEW: AR camera guidance page (mobile-only UX)
import ArcapsuleViewPage from "./components/pages/ar/ArcapsuleViewPage";
import NotFoundPage from "./components/pages/NotFoundPage";
import { applySeoToDocument, getSeoForRoute } from "./seo";

function AuthUserProfileSync() {
  const { currentUser } = useAuth();
  const setUserProfile = useUserProfileStore(selectSetUserProfile);
  const clearUserProfile = useUserProfileStore(selectClearUserProfile);
  const storedUserProfileId = useUserProfileStore(selectUserProfileId);

  React.useEffect(() => {
    if (!currentUser?.uid) {
      if (storedUserProfileId) {
        clearUserProfile();
      }
      return undefined;
    }

    // Identity-only fallback. The auth user knows nothing about subscriptions, so
    // this write merges over the stored profile rather than replacing it (see
    // buildAuthFallbackProfile) and leaves the sync status unresolved — otherwise a
    // transient Firestore failure strips Insider status and the app starts gating
    // paying members out of member-exclusive capsules.
    const applyAuthFallbackProfile = (reason, error) => {
      Sentry.captureMessage("[profile-sync] fell back to the auth-only profile", {
        level: "warning",
        tags: { feature: "profile-sync", fallbackReason: reason },
        extra: {
          errorCode: error?.code || null,
          errorMessage: error?.message || String(error || ""),
          hadStoredSubscriptionData: Boolean(
            useUserProfileStore.getState().userProfile?.subscriptionData
          ),
        },
      });
      setUserProfile(currentUser, { source: "auth-fallback" });
    };

    const userRef = doc(db, "users", currentUser.uid);
    const unsubscribe = onSnapshot(
      userRef,
      async (snapshot) => {
        if (snapshot.exists()) {
          const profile = snapshot.data() || {};
          setUserProfile({
            ...profile,
            uid: currentUser.uid,
            profileImage: String(profile.profileImage || currentUser.photoURL || "").trim(),
          });
          return;
        }

        try {
          const profile = await getOrCreateUserProfile(currentUser);
          if (profile) {
            setUserProfile(profile);
          }
        } catch (error) {
          console.warn("[profile-sync] Failed to create/fetch missing profile:", error);
          applyAuthFallbackProfile("doc-missing-repair-failed", error);
        }
      },
      (error) => {
        console.warn("[profile-sync] Failed to subscribe to user profile:", error);
        applyAuthFallbackProfile("snapshot-error", error);
      }
    );

    return () => unsubscribe();
  }, [
    clearUserProfile,
    currentUser,
    currentUser?.displayName,
    currentUser?.email,
    currentUser?.photoURL,
    currentUser?.uid,
    setUserProfile,
    storedUserProfileId,
  ]);

  React.useEffect(() => {
    if (!currentUser?.uid) return;

    claimPendingSharedCapsules().catch((error) => {
      console.warn("[shared-claim-sync] Failed to claim pending shared capsules:", error);
    });
  }, [currentUser?.uid]);

  return null;
}

function GlobalSeoRouteManager() {
  const location = useLocation();

  React.useEffect(() => {
    applySeoToDocument(getSeoForRoute(location.pathname));
  }, [location.pathname]);

  return null;
}

function LegacyRaffleClaimRedirect() {
  const { raffleId } = useParams();
  return <Navigate to={`/raffles/c/${encodeURIComponent(raffleId || "")}`} replace />;
}

function App() {
  useLastActive();
  const {
    appConfigReady,
    firstRunOnboardingEnabled,
    firstRunOnboardingMode,
  } = useAppConfigFlags();

  return (
    <Router>
      <AuthUserProfileSync />
      <FcmTokenManager />
      <GlobalSeoRouteManager />
      <ScrollToTop />
      <PromoRedeemModalHost />
      <UserBadgesProvider>
      <Routes>
        {/* Authentication Routes */}
        <Route
          path="/signin"
          element={
            <MainLayout>
              <SignInPage />
            </MainLayout>
          }
        />
        <Route
          path="/signup"
          element={
            <MainLayout>
              <SignUpPage />
            </MainLayout>
          }
        />
        <Route
          path="/phone-otp"
          element={
            <MainLayout>
              <PhoneOtpPage />
            </MainLayout>
          }
        />

        {/* Public App Routes */}
        <Route
          path="/"
          element={
            <FirstRunOnboardingGate
              configReady={appConfigReady}
              enabled={firstRunOnboardingEnabled}
              mode={firstRunOnboardingMode}
            >
              <MapLayout>
                <HomePage />
              </MapLayout>
            </FirstRunOnboardingGate>
          }
        />
        <Route
          path="/capsule/:id"
          element={
            <MainLayout showBottomNav={false}>
              <PinDetailsPage />
            </MainLayout>
          }
        />
        <Route
          path="/u/me"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyProfileRedirectPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/u/:handle"
          element={
            <MainLayout>
              <PublicProfilePage />
            </MainLayout>
          }
        />
        <Route
          path="/profile/:uid"
          element={
            <MainLayout>
              <ProfileUidRedirectPage />
            </MainLayout>
          }
        />
        <Route
          path="/business/:businessId"
          element={
            <MainLayout>
              <PublicBusinessPage />
            </MainLayout>
          }
        />

        {/* NEW: AR Camera Guide (mobile-only) */}
        <Route
          path="/ar/capsule/:capsuleId"
          element={
            <NoNavLayout>
              <ArcapsuleViewPage />
            </NoNavLayout>
          }
        />

        {/* Public customer claim routes */}
        <Route
          path="/coupons/c/:campaignId"
          element={
            <MainLayout>
              <ClaimLandingPage campaignType="coupon" />
            </MainLayout>
          }
        />
        <Route
          path="/raffles/c/:campaignId"
          element={
            <MainLayout>
              <ClaimLandingPage campaignType="raffle" />
            </MainLayout>
          }
        />
        <Route
          path="/coupons/t/:ticketToken"
          element={
            <MainLayout>
              <ClaimTicketPage campaignType="coupon" />
            </MainLayout>
          }
        />
        <Route
          path="/raffles/t/:ticketToken"
          element={
            <MainLayout>
              <ClaimTicketPage campaignType="raffle" />
            </MainLayout>
          }
        />
        <Route path="/raffle/:raffleId" element={<LegacyRaffleClaimRedirect />} />
        <Route path="/raffle/:raffleId/ticket/:entryId" element={<Navigate to="/claims" replace />} />

        {/* Protected User Routes */}
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <MainLayout disableMainScroll>
                <MessagesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/timecapsule"
          element={
            <ProtectedRoute>
              <NoNavLayout>
                <TimeCapsuleWizard />
              </NoNavLayout>
            </ProtectedRoute>
          }
        />

        {/* NEW: Points ledger */}
        <Route
          path="/points-ledger"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PointsLedgerPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/claims"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ClaimsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute>
              <MainLayout>
                <HistoryPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Watch & Video Routes */}
        <Route
          path="/watch"
          element={
            <MainLayout>
              <WatchPage />
            </MainLayout>
          }
        />
        <Route
          path="/series/:id"
          element={
            <MainLayout>
              <SeriesPage />
            </MainLayout>
          }
        />
        <Route
          path="/video/:episodeId"
          element={
            <NoNavLayout>
              <VideoPlayerPage />
            </NoNavLayout>
          }
        />

        {/* Profile & Settings */}
        <Route
          path="/me"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyProfileRedirectPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfileSettingsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/settings"
          element={<Navigate to="/profile/edit" replace />}
        />
        <Route
          path="/profile/connections"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MyContactsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/connections/requests"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ConnectionRequestsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/contacts"
          element={<Navigate to="/profile/connections" replace />}
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AccountSettingsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/membership"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MembershipPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </UserBadgesProvider>
    </Router>
  );
}

export default App;
